import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

function canvasHasInk(canvas) {
  if (!canvas) return false;
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > 0) return true;
  }
  return false;
}

/**
 * Sign already-issued Form 12A / S-RaD / Defect that is missing IQP ink.
 * Applies the same signature to the form + companion inspection report.
 */
export default function DraftSignModal({
  buildingId,
  ssCode,
  recordId,
  label,
  onClose,
  onSuccess,
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';

    const pos = (ev) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      return {
        x: ((clientX - rect.left) / rect.width) * canvas.width,
        y: ((clientY - rect.top) / rect.height) * canvas.height,
      };
    };
    const start = (ev) => {
      ev.preventDefault();
      drawing.current = true;
      const p = pos(ev);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const move = (ev) => {
      if (!drawing.current) return;
      ev.preventDefault();
      const p = pos(ev);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    };
    const end = () => {
      drawing.current = false;
    };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const submit = async () => {
    const canvas = canvasRef.current;
    if (!canvasHasInk(canvas)) {
      toast.error('Please sign before submitting');
      return;
    }
    const signature = canvas.toDataURL('image/png');
    setBusy(true);
    try {
      if (recordId) {
        await api.post(`/paperwork/issued-forms/${recordId}/apply-signature`, { signature });
      } else if (buildingId && ssCode) {
        await api.post(
          `/buildings/${encodeURIComponent(buildingId)}/paperwork/${encodeURIComponent(ssCode)}/apply-signature`,
          { signature }
        );
      } else {
        throw new Error('Missing record or building/SS');
      }
      toast.success('Signature applied — form complete');
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Sign failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 className="text-base font-semibold text-slate-900">IQP signature</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {label || 'Apply signature to draft Form 12A / S-RaD / Defect + inspection report'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-slate-500 hover:text-slate-800" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <canvas
            ref={canvasRef}
            width={480}
            height={140}
            className="w-full border border-gray-300 rounded bg-white touch-none"
            aria-label="IQP signature"
          />
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Apply signature'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
